import { BackLink, Header, LoadingSpinner, Text } from '@psyomics/ui-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useGetAppInfoDetails } from '../hooks/use-get-app-info-query';

const useAppInfo = () => {
  const [appInfo, fetching] = useGetAppInfoDetails();

  return {
    appInfo,
    fetching,
  };
};

const ErrorState = ({ text }: { readonly text: string }) => <Text>{text}</Text>;

const AboutHeader = ({ onBack, text }: { readonly onBack: () => void; readonly text: string }) => (
  <div>
    <BackLink onBack={onBack} text={text} />
  </div>
);

const Heading = ({ title }: { readonly title: string }) => (
  <div className="mb-6">
    <Header level="h1">{title}</Header>
  </div>
);

const HowItWorksSection = ({ title, description }: { readonly title: string; readonly description: string }) => (
  <div className="mb-6">
    <Header className="mb-2" level="h2">
      {title}
    </Header>
    <Text>{description}</Text>
  </div>
);

const SoftwareInfoItem = ({ label, value }: { readonly label: string; readonly value: string }) => (
  <div className="mb-2">
    <Text>
      <span className="font-semibold">{label}: </span>
      <span>{value}</span>
    </Text>
  </div>
);

const ManufacturerInfo = ({ label }: { readonly label: string }) => (
  <div className="mb-2">
    <Text>
      <span className="font-semibold">{label}</span>
    </Text>
    <address className="not-italic mt-1 ml-0">
      <Text>Psyomics Ltd.</Text>
      <Text>7 Quy Court Colliers Lane,</Text>
      <Text>Stow-Cum-Quy,</Text>
      <Text>Cambridge,</Text>
      <Text>England,</Text>
      <Text>CB25 9AU</Text>
    </address>
  </div>
);

const SoftwareInfoSection = ({
  title,
  appNameLabel,
  appVersionLabel,
  manufacturerLabel,
  appInfo,
}: {
  readonly title: string;
  readonly appNameLabel: string;
  readonly appVersionLabel: string;
  readonly manufacturerLabel: string;
  readonly appInfo: { readonly name: string; readonly version: string };
}) => (
  <div>
    <Header className="mb-4" level="h2">
      {title}
    </Header>
    <SoftwareInfoItem label={appNameLabel} value={appInfo.name} />
    <SoftwareInfoItem label={appVersionLabel} value={appInfo.version} />
    <ManufacturerInfo label={manufacturerLabel} />
  </div>
);

function About() {
  const { appInfo, fetching } = useAppInfo();
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (fetching) return <LoadingSpinner />;

  if (!appInfo) return <ErrorState text={t('page.about.noData')} />;

  return (
    <div className="relative flex justify-center">
      <div className="max-w-[680px] w-full mx-auto h-full pt-0 p-6">
        <AboutHeader onBack={() => navigate('/account')} text={t('common.buttons.backToMyAccount')} />
        <Heading title={t('page.about.title')} />
        <HowItWorksSection
          description={t('page.about.howItWorks.description')}
          title={t('page.about.howItWorks.title')}
        />
        <SoftwareInfoSection
          appInfo={appInfo}
          appNameLabel={t('page.about.softwareInfo.appName')}
          appVersionLabel={t('page.about.softwareInfo.appVersion')}
          manufacturerLabel={t('page.about.softwareInfo.manufacturer')}
          title={t('page.about.softwareInfo.title')}
        />
      </div>
    </div>
  );
}

export { About };
